import { Box, Button, Menu, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconCopy,
  IconDots,
  IconEye,
  IconFileText,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import type { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { isNonNullish, isNullish } from 'remeda';
import type { TemplateV2 } from 'shared/hooks/use-load-template-v2';
import { useDeleteTemplate } from '../api';
import { DuplicateTemplateModal } from '../DuplicateTemplateModal';
import { ChangeTemplateTypeModal } from './ChangeTemplateTypeModal';
import { RenameTemplateModal } from './RenameTemplateModal';
import { usePreviewTemplateBody } from './use-preview-template-body';

type TemplateActionsMenuProps = {
  template: TemplateV2;
};

export const TemplateActionsMenu = ({ template }: TemplateActionsMenuProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { deleteTemplate, isDeleteTemplateLoading } = useDeleteTemplate();
  const { preview, canPreview, isPreviewLoading } = usePreviewTemplateBody();

  const openRenameModal = () =>
    modals.open({
      title: t(i18nKeys.TEMPLATE.ACTIONS.CHANGE_NAME_TITLE),
      size: 'sm',
      children: (
        <RenameTemplateModal
          templateId={template.id}
          name={template.name}
          onClose={modals.closeAll}
        />
      ),
    });

  const openChangeTypeModal = () =>
    modals.open({
      title: t(i18nKeys.TEMPLATE.ACTIONS.CHANGE_TYPE_TITLE),
      size: 'sm',
      children: (
        <ChangeTemplateTypeModal
          templateId={template.id}
          planType={template.planType}
          stepType={template.stepType}
          onClose={modals.closeAll}
        />
      ),
    });

  const openDuplicateModal = () =>
    modals.open({
      title: t(i18nKeys.DUPLICATE),
      centered: true,
      children: <DuplicateTemplateModal template={template} onClose={modals.closeAll} />,
    });

  const listPath =
    template.planType === 'payment'
      ? '/settings/templates/payment-plans'
      : '/settings/templates/recovery';

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: t(i18nKeys.DELETE_TEMPLATE),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.AYS.DELETE_TEMPLATE)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red', leftSection: <IconTrash size={16} stroke={1.5} /> },
      onConfirm: () =>
        deleteTemplate({ id: template.id }, { onSuccess: () => history.push(listPath) }),
    });

  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button
          variant="default"
          px="sm"
          aria-label={t(i18nKeys.TEMPLATE.ACTIONS.MORE)}
          loading={isDeleteTemplateLoading}
        >
          <IconDots size={18} stroke={1.5} />
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconPencil size={16} stroke={1.5} />} onClick={openRenameModal}>
          {t(i18nKeys.TEMPLATE.ACTIONS.CHANGE_NAME)}
        </Menu.Item>

        <TooltipMenuItem
          reason={template.noChangeTypeReason}
          leftSection={<IconFileText size={16} stroke={1.5} />}
          onClick={openChangeTypeModal}
        >
          {t(i18nKeys.TEMPLATE.ACTIONS.CHANGE_TYPE)}
        </TooltipMenuItem>

        <Menu.Item leftSection={<IconCopy size={16} stroke={1.5} />} onClick={openDuplicateModal}>
          {t(i18nKeys.DUPLICATE)}
        </Menu.Item>

        <Menu.Item
          leftSection={<IconEye size={16} stroke={1.5} />}
          onClick={preview}
          disabled={!canPreview || isPreviewLoading}
        >
          {t(i18nKeys.TEMPLATE.ACTIONS.PREVIEW)}
        </Menu.Item>

        <Menu.Divider />

        <TooltipMenuItem
          reason={template.noDeletionReason}
          color="red"
          leftSection={<IconTrash size={16} stroke={1.5} />}
          onClick={confirmDelete}
        >
          {t(i18nKeys.DELETE)}
        </TooltipMenuItem>
      </Menu.Dropdown>
    </Menu>
  );
};

type TooltipMenuItemProps = {
  reason: string | null;
  color?: string;
  leftSection: ReactNode;
  onClick: () => void;
  children: ReactNode;
};

const TooltipMenuItem = ({
  reason,
  color,
  leftSection,
  onClick,
  children,
}: TooltipMenuItemProps) => (
  <Tooltip label={reason} disabled={isNullish(reason)} multiline maw={320} position="left">
    <Box>
      <Menu.Item
        color={color}
        leftSection={leftSection}
        onClick={onClick}
        disabled={isNonNullish(reason)}
      >
        {children}
      </Menu.Item>
    </Box>
  </Tooltip>
);
